/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import { debounce } from "lodash";
import * as yup from 'yup';
import Icon from 'components/Icon';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  InlineLabel,
  DialogIcon,
  ViewText,
  RadioContainer,
  CalendarContainer,
  ButtonContainer,
  ButtonContent,
  LabelEdit,
  BorderSwitch
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import CheckBox from "components/Form/CheckBox";
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import ComboBox from 'components/Form/ComboBox3';
import InputField from 'components/Form/InputField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import { RadioButton } from "components/Form/Radio/index";
import {DayPicker} from 'react-day-picker';
import SearchField from 'components/Form/SearchField';
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import Switch from 'react-switch';
import Colors from 'constants/Colors';

const InsurerInformation = ({ idPerson, columns, data, hasPermission, onSavePersonRelationship, dataRunOff, columnsRunOff }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const [validations, setValidations] = useState({});

  const [sortBy, setSortBy] = useState({ key: null, direction: null, });
  const [addPersonRelationship, setAddPersonRelationship] = useState(false);
  const [idPersonRelationship, setIdPersonRelationship] = useState(null);
  const [idPersonRelationshipRunOff, setIdPersonRelationshipRunOff] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [searchList, setSearchList] = useState([])
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [alreadyExistsRunOff, setAlreadyExistsRunOff] = useState(false);

  const [initial, setInitial] = useState({
    ndaStatus: '',
    capacityExposure: '',
    ndaExpireDate: '',
    personTailLastName: '',
    idPersonTail: '',
    runOff: false,
  });

  const marketOptions = [
    { label: "Lirma Market", value: "lirma" },
    { label: "Lloyds Market", value: "lloyds" },
    { label: "MAS", value: "mas" },
  ]

  const setEditPersonRelationship = (personRelationship) => {
    const isLirma = personRelationship.relationshipContent.lirmaType;
    const isLloyds = personRelationship.relationshipContent.lloydsType;
    const isMAS = personRelationship.relationshipContent.masLicenceType;

    formikRef.current.values.lloydsSyndicateCode = personRelationship.relationshipContent.lloydsSyndicateCode;
    formikRef.current.values.personTailLastName = personRelationship.personTailLastName;
    formikRef.current.values.idPersonTail = personRelationship.idPersonTail;
    formikRef.current.values.marketType = isLirma ? "lirma" : isLloyds ? "lloyds" : isMAS ? "mas" : null;
    formikRef.current.values.runOff = personRelationship.relationshipContent?.runOff ? true : false; 

    setAlreadyExistsRunOff(personRelationship.relationshipContent?.runOff ? true : false);
    setAddPersonRelationship(true);
    setIdPersonRelationship(personRelationship.idPersonRelationship);
    setIdPersonRelationshipRunOff(personRelationship?.idPersonRelationshipRunOff ? personRelationship?.idPersonRelationshipRunOff : null);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100)
  }

  const resetForm = () => {
    formikRef.current.values.marketType = '';
    formikRef.current.values.lloydsSyndicateCode = '';
    formikRef.current.values.personTailLastName = '';
    formikRef.current.values.idPersonTail = '';
    formikRef.current.values.runOff = false;
  }

  const saveRelationship = async (body) => {
    try {
      loading.start();
      body.idPersonHead = idPerson;
      body.idPersonRelationship = idPersonRelationship;
      body.idDomain = 365;

      const isLirma = body.marketType === "lirma";
      const isLloyds = body.marketType === "lloyds";
      const isMAS = body.marketType === "mas";

      body.relationshipContent = {
        lirmaType: isLirma,
        lloydsType: isLloyds,
        lloydsSyndicateCode: body.lloydsSyndicateCode,
        masLicenceType: isMAS
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      const bodyRunOff = {}; 
      bodyRunOff.idPersonHead = idPerson;
      bodyRunOff.idPersonRelationship = idPersonRelationshipRunOff || null;
      bodyRunOff.idDomain = 628;
      bodyRunOff.idPersonTail = body.idPersonTail;
      bodyRunOff.relationshipContent = {}
   
      if (body.runOff) { 
        if (!alreadyExistsRunOff) {
          const responseRelationShipRunOff = await fitinsur.requestSavePersonRelationship(bodyRunOff).then(res => res.json());
          if (responseRelationShipRunOff.ret_code !== 200) {
            mc.error(responseRelationShipRunOff.ret_msg_details)
            loading.stop();
            return;
          }      
        }
      } else {
        if (alreadyExistsRunOff) {
          deletePersonRelationship(idPersonRelationshipRunOff, false);
        }
      }

      setIdPersonRelationship(null);
      setIdPersonRelationshipRunOff(null);
      setAddPersonRelationship(false);
      setAlreadyExistsRunOff(false);
      resetForm();
      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const deletePersonRelationship = async (idPersonRelationship, idPersonRelationshipRunOff = false) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeletePersonRelationship(idPersonRelationship).then(res => res.json());
      setIdPersonRelationship(null);

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      if (idPersonRelationshipRunOff) {
        const responseRunOff = await fitinsur.requestDeletePersonRelationship(idPersonRelationshipRunOff).then(res => res.json());
        setIdPersonRelationshipRunOff(null);
  
        if (responseRunOff.ret_code !== 200) {
          mc.error(responseRunOff.ret_msg_details)
          loading.stop();
          return;
        }  
      }

      setAlreadyExistsRunOff(false);

      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      setIdPersonRelationship(null);
      setIdPersonRelationshipRunOff(null);
      setAlreadyExistsRunOff(false);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const searchCompanies = async (searchValue) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestGetPersonsList(`?fullName=${searchValue}&idPersonRoot=${idPerson}&personType=35`).then((response) => response.json()).then((data) => {
        if (data.ret_code != 200) {
          return;
        }

        data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.lastName}` }))
        setSearchList(data.ret_value);
      });
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      idPersonTail: yup.string().required("* Required field"),
      lloydsSyndicateCode: yup.string().when('marketType', (marketType) => !marketType || marketType === 'mas' ? yup.string().notRequired() : yup.string().required("* Required field"))
    });
    setValidations(validations);
    return validations
  }

  const debouncedChangeHandler = useCallback(
    debounce(searchCompanies, 300)
    , []);

  useEffect(() => {
    createValidation();
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveRelationship(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIdPersonRelationship(null); setIdPersonRelationshipRunOff(null); setAlreadyExistsRunOff(false); }} onConfirm={() => deletePersonRelationship(idPersonRelationship, idPersonRelationshipRunOff)} />
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() => data.map((item, index) => (
                  <TableRow key={index} >
                    <TableCollum>
                      <InlineLabel>
                        Insurer Legal Entity
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.personTailLastName}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        MAS
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.masLicenceType ? "YES" : "NO"}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Lirma Type
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.lirmaType ? "YES" : "NO"}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Lloyds Type
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.lloydsType ? "YES" : "NO"}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Lloyds Syndicate Code
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.lloydsSyndicateCode}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum right>
                      <InlineLabel>
                        ACTION
                      </InlineLabel>
                      <Label>
                        <Icon
                          name="edit"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => setEditPersonRelationship(item)}
                        />
                        <Icon
                          name="trash"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => {
                            setIdPersonRelationship(item.idPersonRelationship);
                            setIdPersonRelationshipRunOff(item.idPersonRelationshipRunOff);
                            setAlreadyExistsRunOff(item?.idPersonRelationshipRunOff ? true : false);
                            setDeleteModalOpen(true);
                            setAddPersonRelationship(false);
                            resetForm();
                          }} />
                      </Label>
                    </TableCollum>
                  </TableRow>
                ))
                }
              />
            </TableBody>
          </Table>

          <Table style={{marginTop: "10px"}}>
            <TableHead>
              <TableRow>
                {columnsRunOff.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() => dataRunOff.map((item, index) => (
                  <TableRow key={index} >
                    <TableCollum width={"31.5%"}>
                      <InlineLabel>
                        Insurer Legal Entity
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.personTailLastName}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Run-Off
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent?.runOff ? "YES" : "NO"}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum right>
                      <InlineLabel>
                      </InlineLabel>
                      <Label>
                      </Label>
                    </TableCollum>
                  </TableRow>
                ))
                }
              />
            </TableBody>
          </Table>

          {!addPersonRelationship && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddPersonRelationship(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}

          {addPersonRelationship && <Form>
            <div style={{ marginTop: 40, marginBottom: 10 }}>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="personTailLastName"
                    label="Insurer Legal Entity"
                    isRequired
                    component={SearchField}
                    disabled={idPersonRelationship}
                    searchList={searchList}
                    value={values.personTailLastName && values.personTailLastName}
                    onChange={debouncedChangeHandler}
                    onFocus={() => {
                      setSearchList([]);
                      searchCompanies("");
                    }}
                    canGenerate={false}
                    onSearchSelect={(selectedValue) => {
                      setFieldValue(`personTailLastName`, selectedValue.lastName);
                      setFieldValue(`idPersonTail`, selectedValue.idPerson);
                      setSearchList([]);
                    }}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="marketType"
                    label="Market"
                    isRequired={true}
                    options={marketOptions || []}
                    searchList={marketOptions || []}
                    onChange={(e) => { setFieldValue(`marketType`, e); }}
                    component={ComboBox}
                    disabled={!hasPermission}
                  />
                </Col>
              </Row>

              <Row>
                {values.marketType !== 'mas' &&
                  <Col lg={6} md={6} sm={6} xs={12}>
                    <Field
                      name="lloydsSyndicateCode"
                      label="Syndicate Code"
                      component={InputField}
                      disabled={!values.marketType}
                    />
                  </Col>
                }
                  <Col lg={6} md={6} sm={6} xs={12} style={{display: "inline-flex", alignItems: "center", marginBottom: "20px"}}>
                    <Label style={{ marginRight: "15px"}}>Run-off</Label>
                      <BorderSwitch>
                        <Field
                          name="runOff"
                          component={Switch}
                          checked={values?.runOff}
                          height={27}
                          width={55}
                          onChange={e => {
                            setFieldValue('runOff', e);
                          }}
                          checkedIcon={false}
                          uncheckedIcon={false}
                          handleDiameter={20}
                          offColor={Colors.WHITE}
                          onColor={Colors.GRAY[6]}
                          offHandleColor={Colors.GRAY[6]}
                          onHandleColor={Colors.WHITE}
                        />
                      </BorderSwitch>
                  </Col>
              </Row>


              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    third
                    onClick={() => {
                      setIdPersonRelationship(null);
                      setIdPersonRelationshipRunOff(null);
                      setAlreadyExistsRunOff(false);
                      setAddPersonRelationship(false);
                      resetForm();
                    }}
                  >
                    CANCEL
                  </Button>
                </ButtonContent>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    {idPersonRelationship ? "SAVE" : "ADD"}
                  </Button>
                </ButtonContent>
              </ButtonContainer>
            </div>
          </Form>}
        </>
      )
      }
    </Formik >
  )
}

export default InsurerInformation;